/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Navigation Menu Management View (Task 3.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Compass,
  Plus,
  Save,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  Edit2,
  Trash2,
  ExternalLink,
  Check,
  AlertTriangle,
  X,
  Link,
  Eye,
  Layers,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import type {
  NavigationMenu,
  NavigationItem,
  UpdateNavigationMenuInput,
} from '@sofia/shared-types';

const DEFAULT_MENU: NavigationMenu = {
  header: [
    { id: 'nav-home', label: 'Home', url: '/', target: '_self', isExternal: false, sortOrder: 1 },
    { id: 'nav-features', label: 'Features', url: '/#features', target: '_self', isExternal: false, sortOrder: 2 },
    { id: 'nav-pricing', label: 'Pricing', url: '/#pricing', target: '_self', isExternal: false, sortOrder: 3 },
    { id: 'nav-blog', label: 'Blog', url: '/blog', target: '_self', isExternal: false, sortOrder: 4 },
    { id: 'nav-about', label: 'About', url: '/about', target: '_self', isExternal: false, sortOrder: 5 },
  ],
  footer: [
    { id: 'nav-privacy', label: 'Privacy Policy', url: '/privacy', target: '_self', isExternal: false, sortOrder: 1 },
    { id: 'nav-terms', label: 'Terms of Service', url: '/terms', target: '_self', isExternal: false, sortOrder: 2 },
    { id: 'nav-contact', label: 'Contact', url: '/contact', target: '_self', isExternal: false, sortOrder: 3 },
  ],
};

export const NavigationMenuManagementView: React.FC = () => {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [headerItems, setHeaderItems] = useState<NavigationItem[]>([]);
  const [footerItems, setFooterItems] = useState<NavigationItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Modal State
  const [modalType, setModalType] = useState<'header' | 'footer' | null>(null);
  const [editingItem, setEditingItem] = useState<{ section: 'header' | 'footer'; item: NavigationItem } | null>(null);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 4000);
  };

  const { data: menuData } = useQuery<NavigationMenu>({
    queryKey: ['admin-cms-navigation'],
    queryFn: async () => {
      const res = await adminClient.get('/api/v1/admin/cms/navigation');
      return res.data || DEFAULT_MENU;
    },
  });

  useEffect(() => {
    if (menuData) {
      setHeaderItems(menuData.header || []);
      setFooterItems(menuData.footer || []);
      setHasChanges(false);
    }
  }, [menuData]);

  const saveMutation = useMutation({
    mutationFn: (payload: UpdateNavigationMenuInput) =>
      adminClient.put('/api/v1/admin/cms/navigation', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cms-navigation'] });
      setHasChanges(false);
      showToast('success', t('admin.cms.navSaved') || 'Navigation menu saved and synced to edge cache.');
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to save navigation.');
    },
  });

  const handleSave = () => {
    saveMutation.mutate({
      header: headerItems.map((item, idx) => ({ ...item, sortOrder: idx + 1 })),
      footer: footerItems.map((item, idx) => ({ ...item, sortOrder: idx + 1 })),
    });
  };

  const handleResetDefaults = () => {
    setHeaderItems([...DEFAULT_MENU.header]);
    setFooterItems([...DEFAULT_MENU.footer]);
    setHasChanges(true);
  };

  // Reorder helpers
  const moveItem = (section: 'header' | 'footer', index: number, direction: 'up' | 'down') => {
    const items = section === 'header' ? [...headerItems] : [...footerItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    const currentItem = items[index];
    const targetItem = items[targetIndex];
    if (!currentItem || !targetItem) return;

    items[index] = targetItem;
    items[targetIndex] = currentItem;

    if (section === 'header') {
      setHeaderItems(items);
    } else {
      setFooterItems(items);
    }
    setHasChanges(true);
  };

  const deleteItem = (section: 'header' | 'footer', id: string) => {
    if (section === 'header') {
      setHeaderItems(headerItems.filter((i) => i.id !== id));
    } else {
      setFooterItems(footerItems.filter((i) => i.id !== id));
    }
    setHasChanges(true);
  };

  const handleSaveItem = (itemData: Omit<NavigationItem, 'id' | 'sortOrder'>) => {
    if (editingItem) {
      const section = editingItem.section;
      const updated = {
        ...editingItem.item,
        ...itemData,
      };

      if (section === 'header') {
        setHeaderItems(headerItems.map((i) => (i.id === updated.id ? updated : i)));
      } else {
        setFooterItems(footerItems.map((i) => (i.id === updated.id ? updated : i)));
      }
      setEditingItem(null);
    } else if (modalType) {
      const newItem: NavigationItem = {
        id: `nav_${Date.now()}_${crypto.randomUUID().slice(0, 4)}`,
        sortOrder: (modalType === 'header' ? headerItems.length : footerItems.length) + 1,
        ...itemData,
      };

      if (modalType === 'header') {
        setHeaderItems([...headerItems, newItem]);
      } else {
        setFooterItems([...footerItems, newItem]);
      }
      setModalType(null);
    }
    setHasChanges(true);
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl border shadow-xl text-xs font-semibold backdrop-blur-md animate-fade-in ${
            toastMessage.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
              : 'bg-destructive/10 border-destructive/30 text-destructive'
          }`}
        >
          {toastMessage.type === 'success' ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-surface-border">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-heading font-bold text-foreground">
              {t('admin.cms.navTitle') || 'Navigation Menus'}
            </h2>
            {hasChanges && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 animate-pulse">
                Unsaved Changes
              </span>
            )}
          </div>
          <p className="text-xs text-muted-fg mt-1">
            {t('admin.cms.navSubtitle') || 'Customize Navbar and Footer navigation links for web-landing templates.'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleResetDefaults}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-surface-border text-muted-fg hover:text-foreground hover:bg-surface-elevated text-xs font-medium transition-all"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Reset Defaults</span>
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || saveMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-primary text-white font-semibold text-xs hover:bg-primary/90 shadow-sm transition-all disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" />
            <span>{saveMutation.isPending ? 'Saving...' : 'Save Navigation'}</span>
          </button>
        </div>
      </div>

      {/* Main Grid: Header Menu & Footer Menu */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 1. Header Navbar Links */}
        <div className="rounded-2xl bg-surface border border-surface-border p-5 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-surface-border">
            <div className="flex items-center gap-2">
              <Compass className="w-4 h-4 text-primary" />
              <h3 className="font-heading font-semibold text-sm text-foreground">
                Header Navbar Links
              </h3>
              <span className="text-[10px] text-muted-fg font-mono">({headerItems.length} links)</span>
            </div>
            <button
              type="button"
              onClick={() => {
                setEditingItem(null);
                setModalType('header');
              }}
              className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-primary/10 text-primary hover:bg-primary/20 font-medium transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add Link</span>
            </button>
          </div>

          <div className="space-y-2">
            {headerItems.map((item, index) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated border border-surface-border text-xs group hover:border-primary/40 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex flex-col items-center gap-0.5">
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => moveItem('header', index, 'up')}
                      className="p-0.5 text-muted-fg hover:text-foreground disabled:opacity-20"
                    >
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      disabled={index === headerItems.length - 1}
                      onClick={() => moveItem('header', index, 'down')}
                      className="p-0.5 text-muted-fg hover:text-foreground disabled:opacity-20"
                    >
                      <ArrowDown className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground truncate">{item.label}</span>
                      {item.isExternal && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.2 rounded bg-surface border border-surface-border text-muted-fg font-mono">
                          <ExternalLink className="w-2.5 h-2.5" />
                          ext
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-fg font-mono truncate">{item.url}</span>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEditingItem({ section: 'header', item })}
                    className="p-1.5 text-muted-fg hover:text-foreground rounded-lg hover:bg-surface"
                    title="Edit link"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteItem('header', item.id)}
                    className="p-1.5 text-muted-fg hover:text-destructive rounded-lg hover:bg-destructive/10"
                    title="Delete link"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}

            {headerItems.length === 0 && (
              <div className="p-8 text-center border border-dashed border-surface-border rounded-xl text-xs text-muted-fg">
                No links in header. Click "Add Link" to populate navbar.
              </div>
            )}
          </div>
        </div>

        {/* 2. Footer Menu Links */}
        <div className="rounded-2xl bg-surface border border-surface-border p-5 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-surface-border">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-accent" />
              <h3 className="font-heading font-semibold text-sm text-foreground">
                Footer Links
              </h3>
              <span className="text-[10px] text-muted-fg font-mono">({footerItems.length} links)</span>
            </div>
            <button
              type="button"
              onClick={() => {
                setEditingItem(null);
                setModalType('footer');
              }}
              className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-primary/10 text-primary hover:bg-primary/20 font-medium transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add Link</span>
            </button>
          </div>

          <div className="space-y-2">
            {footerItems.map((item, index) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated border border-surface-border text-xs group hover:border-accent/40 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex flex-col items-center gap-0.5">
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => moveItem('footer', index, 'up')}
                      className="p-0.5 text-muted-fg hover:text-foreground disabled:opacity-20"
                    >
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      disabled={index === footerItems.length - 1}
                      onClick={() => moveItem('footer', index, 'down')}
                      className="p-0.5 text-muted-fg hover:text-foreground disabled:opacity-20"
                    >
                      <ArrowDown className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground truncate">{item.label}</span>
                      {item.isExternal && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.2 rounded bg-surface border border-surface-border text-muted-fg font-mono">
                          <ExternalLink className="w-2.5 h-2.5" />
                          ext
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-fg font-mono truncate">{item.url}</span>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEditingItem({ section: 'footer', item })}
                    className="p-1.5 text-muted-fg hover:text-foreground rounded-lg hover:bg-surface"
                    title="Edit link"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteItem('footer', item.id)}
                    className="p-1.5 text-muted-fg hover:text-destructive rounded-lg hover:bg-destructive/10"
                    title="Delete link"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}

            {footerItems.length === 0 && (
              <div className="p-8 text-center border border-dashed border-surface-border rounded-xl text-xs text-muted-fg">
                No links in footer. Click "Add Link" to populate footer.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Visual Simulation Card */}
      <div className="rounded-2xl bg-surface border border-surface-border p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-sm text-foreground">
            Landing Page Live Simulation
          </h3>
        </div>

        {/* Navbar Simulation */}
        <div className="p-4 rounded-xl bg-surface-elevated border border-surface-border flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-xs text-foreground font-heading">
            <div className="w-6 h-6 rounded bg-primary text-white flex items-center justify-center text-[11px]">S</div>
            <span>Sophia</span>
          </div>
          <div className="flex items-center gap-4 text-xs font-medium text-muted-fg">
            {headerItems.map((item) => (
              <span key={item.id} className="hover:text-foreground cursor-pointer transition-colors">
                {item.label}
              </span>
            ))}
          </div>
          <div className="px-3 py-1 text-xs font-semibold rounded-lg bg-primary text-white">
            Get Started
          </div>
        </div>

        {/* Footer Simulation */}
        <div className="p-4 rounded-xl bg-surface-elevated border border-surface-border flex flex-col sm:flex-row items-center justify-between text-xs text-muted-fg gap-2">
          <span>(c) 2026 Four Elements Corp. All rights reserved.</span>
          <div className="flex items-center gap-3">
            {footerItems.map((item) => (
              <span key={item.id} className="hover:text-foreground cursor-pointer">
                {item.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Link Edit / Add Modal */}
      {(modalType || editingItem) && (
        <NavItemModal
          initialData={editingItem?.item}
          sectionName={editingItem ? editingItem.section : (modalType || 'header')}
          isOpen={Boolean(modalType || editingItem)}
          onClose={() => {
            setModalType(null);
            setEditingItem(null);
          }}
          onSave={handleSaveItem}
        />
      )}
    </div>
  );
};

// ============================================================================
// Navigation Item Modal
// ============================================================================

interface NavItemModalProps {
  initialData?: NavigationItem;
  sectionName: 'header' | 'footer';
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Omit<NavigationItem, 'id' | 'sortOrder'>) => void;
}

const NavItemModal: React.FC<NavItemModalProps> = ({
  initialData,
  sectionName,
  isOpen,
  onClose,
  onSave,
}) => {
  const [label, setLabel] = useState(initialData?.label || '');
  const [url, setUrl] = useState(initialData?.url || '');
  const [target, setTarget] = useState<'_self' | '_blank'>(initialData?.target || '_self');
  const [isExternal, setIsExternal] = useState(initialData?.isExternal || false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      label: label.trim(),
      url: url.trim(),
      target,
      isExternal,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md rounded-2xl bg-surface border border-surface-border shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-surface-border pb-3">
          <div className="flex items-center gap-2">
            <Link className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-semibold text-sm text-foreground">
              {initialData ? `Edit ${sectionName} Link` : `Add ${sectionName} Link`}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3.5">
          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              Link Title / Label *
            </label>
            <input
              type="text"
              required
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Documentation"
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              Destination URL / Path *
            </label>
            <input
              type="text"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="e.g. /#pricing, /about or https://..."
              className="w-full px-3 py-2 text-xs font-mono rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                Link Target
              </label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value as '_self' | '_blank')}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              >
                <option value="_self">Same Tab (_self)</option>
                <option value="_blank">New Tab (_blank)</option>
              </select>
            </div>

            <div className="flex flex-col justify-end">
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={isExternal}
                  onChange={(e) => setIsExternal(e.target.checked)}
                  className="rounded border-surface-border text-primary focus:ring-primary"
                />
                <span className="text-xs text-foreground font-medium">External Link</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-surface-border">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 shadow-sm"
            >
              {initialData ? 'Update Link' : 'Add Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
